
import React, { useState, useEffect } from 'react';
import { MemberModel, BranchModel, SiteSettingModel, ScheduleEvent, StudentCounselModel, ActiveTab, BookModel, NoticeModel, InquiryModel, CompanyName, COLLECTIONS } from '../../../models';
import Sidebar, { MenuItem } from '../frame/Sidebar';
import ScheduleManagement from './schedule/ScheduleManagement';
import MemberRoot from './studentMember/studentMemberRoot';
import CounselRoot from './counsel/CounselRoot';
import NoticeManagement from './notice/noticeRoot';
import HQNoticeView from './notice/HQNoticeView'; // Import
import InquiryRoot from './inquiry/inquiryRoot';
import SuccessRoot from './success/successRoot';
import BookManagement from '../super/book/BookManagement';
import AcademyLifeRoot from '../shared/academyLifeRoot';
import ReviewsRoot from '../shared/reviewsRoot';
import BannersRoot from '../shared/bannersRoot';
import { auth, db } from '../../../firebase';
import { collection, onSnapshot, query, orderBy, setDoc, doc, deleteDoc, writeBatch, updateDoc, where, getDoc } from 'firebase/firestore';
import { Users, Calendar, Headphones, Bell, MessageSquare, GraduationCap, BookOpen, Settings, History, Camera, Star, Sliders } from 'lucide-react';

import SettingRoot from './settings/settingRoot';
import LogRoot from './log/LogRoot';
import { logActivity } from '../../../services/logService';

interface ManagerRootProps {
    onGoHome?: () => void;
    currentAdmin: MemberModel;
}

const ManagerRoot: React.FC<ManagerRootProps> = ({ onGoHome, currentAdmin }) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('sidebar-collapsed') === 'true';
    });

    const toggleSidebar = () => {
        const newState = !isSidebarCollapsed;
        setIsSidebarCollapsed(newState);
        localStorage.setItem('sidebar-collapsed', String(newState));
    };

    const [activeTab, setActiveTab] = useState<ActiveTab>('members');
    const [members, setMembers] = useState<MemberModel[]>([]);
    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [schedules, setSchedules] = useState<ScheduleEvent[]>([]);
    const [counsels, setCounsels] = useState<StudentCounselModel[]>([]);
    const [notices, setNotices] = useState<NoticeModel[]>([]);
    const [inquiries, setInquiries] = useState<InquiryModel[]>([]);
    const [books, setBooks] = useState<BookModel[]>([]);
    const [editingBook, setEditingBook] = useState<BookModel | null>(null);
    const [selectedMember, setSelectedMember] = useState<MemberModel | null>(null);
    const [siteSettings, setSiteSettings] = useState<SiteSettingModel>({
        subjects: [], grades: [], bookTypes: [], publishers: [], examTypes: []
    });

    // New state for HQ Notices
    const [hqNotices, setHqNotices] = useState<NoticeModel[]>([]);
    const [readNoticeIds, setReadNoticeIds] = useState<string[]>([]);

    const [localAdmin, setLocalAdmin] = useState<MemberModel | null>(currentAdmin || null);

    useEffect(() => {
        if (currentAdmin) {
            setLocalAdmin(currentAdmin);
        }
    }, [currentAdmin]);

    useEffect(() => {
        if (localAdmin) return;

        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    const docRef = doc(db, COLLECTIONS.MEMBERS, user.uid);
                    const snap = await getDoc(docRef);
                    if (snap.exists()) {
                        setLocalAdmin({ id: snap.id, ...snap.data() } as MemberModel);
                    } else {
                        // Fallback: 임시 관리자 데이터 생성
                        setLocalAdmin({
                            id: user.uid,
                            name: user.displayName || "임시 지점관리자",
                            email: user.email || "",
                            role: "teacher",
                            status: "active"
                        } as MemberModel);
                    }
                } catch (e) {
                    console.error("Profile recovery error:", e);
                    // Fallback on error
                    setLocalAdmin({
                        id: user.uid,
                        name: user.displayName || "임시 지점관리자",
                        email: user.email || "",
                        role: "teacher",
                        status: "active"
                    } as MemberModel);
                }
            } else {
                // 로그인 세션이 없는 경우 임시 mock 지점관리자 주입
                console.log("No authenticated user, injecting mock manager for development...");
                setLocalAdmin({
                    id: "mock_manager_id",
                    name: "임시 지점관리자 (개발용)",
                    email: "teacher@test.com",
                    role: "teacher",
                    status: "active"
                } as MemberModel);
            }
        });
        return () => unsubscribe();
    }, [localAdmin]);

    // Data Fetching
    useEffect(() => {
        if (!localAdmin) return;

        // 1. Global Data (Doesn't depend on currentAdmin)
        const settingsDocRef = doc(db, 'settings', 'site');
        const unsubscribeSettings = onSnapshot(settingsDocRef, (snapshot) => {
            if (snapshot.exists()) setSiteSettings(snapshot.data() as SiteSettingModel);
        });

        const booksQuery = query(collection(db, COLLECTIONS.BOOKS), orderBy('createdAt', 'desc'));
        const unsubscribeBooks = onSnapshot(booksQuery, (snapshot) => {
            const list: BookModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as BookModel));
            setBooks(list);
        }, (error) => {
            console.error("Fetch books error:", error);
            alert("교재 목록 로드 실패: " + error.message);
        });

        // 2. Fetch Branches (Global for lookup)
        const branchesQuery = query(collection(db, 'branches'));
        const unsubscribeBranches = onSnapshot(branchesQuery, (snapshot) => {
            const list: BranchModel[] = [];
            snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as BranchModel));
            setBranches(list);
        });

        // 3. Admin-Dependent Data
        let unsubscribeMembers = () => { };
        let unsubscribeSchedules = () => { };
        let unsubscribeCounsels = () => { };
        let unsubscribeNotices = () => { };
        let unsubscribeInquiries = () => { };
        let unsubscribeRead = () => { };

        const targetBranchId = 'main';

        if (targetBranchId) {
            // Members
            const membersQuery = query(collection(db, COLLECTIONS.MEMBERS));
            unsubscribeMembers = onSnapshot(membersQuery, (snapshot) => {
                const list: MemberModel[] = [];
                snapshot.forEach((doc) => list.push({ ...doc.data(), id: doc.id } as MemberModel));
                setMembers(list);
            });

            // Schedules
            const schedulesQuery = query(collection(db, COLLECTIONS.STUDENT_SCHEDULES));
            unsubscribeSchedules = onSnapshot(schedulesQuery, (snapshot) => {
                const list: ScheduleEvent[] = [];
                snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as ScheduleEvent));
                setSchedules(list);
            });

            // Counsels
            const counselsQuery = query(collection(db, COLLECTIONS.STUDENT_COUNSEL));
            unsubscribeCounsels = onSnapshot(counselsQuery, (snapshot) => {
                const list: StudentCounselModel[] = [];
                snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as StudentCounselModel));
                setCounsels(list);
            });

            // Notices - Fetch ALL and separate
            const noticesQuery = query(collection(db, COLLECTIONS.NOTICES), orderBy('createdAt', 'desc'));
            unsubscribeNotices = onSnapshot(noticesQuery, (snapshot) => {
                const list: NoticeModel[] = [];
                snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as NoticeModel));
                // Filter for Student Notices (Management Tab)
                const studentList = list.filter(n => n.targetAudience.includes('student'));
                setNotices(studentList);

                // Filter for HQ Notices (targeted at manager/staff)
                const hqList = list.filter(n => {
                    const isTarget = n.targetAudience.includes('manager');
                    const isBranchMatch = !n.branchId || n.branchId === 'all' || n.branchId === targetBranchId;
                    return isTarget && isBranchMatch;
                });
                setHqNotices(hqList);
            });

            // Fetch Read Status for Badge
            const readQuery = collection(db, COLLECTIONS.MEMBERS, localAdmin.id, 'read_notices');
            unsubscribeRead = onSnapshot(readQuery, (snap) => {
                const readIds: string[] = [];
                snap.forEach(doc => readIds.push(doc.id));
                setReadNoticeIds(readIds);
            });

            // Inquiries
            const inquiriesQuery = query(collection(db, COLLECTIONS.INQUIRIES));
            unsubscribeInquiries = onSnapshot(inquiriesQuery, (snapshot) => {
                const list: InquiryModel[] = [];
                snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() } as InquiryModel));
                setInquiries(list);
            });
        }

        return () => {
            unsubscribeSettings();
            unsubscribeBooks();
            unsubscribeMembers();
            unsubscribeBranches();
            unsubscribeSchedules();
            unsubscribeCounsels();
            unsubscribeNotices();
            unsubscribeInquiries();
            unsubscribeRead();
        };
    }, [localAdmin]);


    const addMember = async (member: MemberModel) => {
        try { await setDoc(doc(db, COLLECTIONS.MEMBERS, member.id), member); } catch (e) { console.error(e); }
    };

    const updateMember = async (member: MemberModel) => {
        try {
            await setDoc(doc(db, COLLECTIONS.MEMBERS, member.id), member);
            setSelectedMember(member);
        } catch (e) { console.error(e); }
    };

    const deleteMember = async (id: string) => {
        if (!confirm('학생 정보를 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, COLLECTIONS.MEMBERS, id));
            if (selectedMember?.id === id) setSelectedMember(null);
        } catch (e) { console.error(e); }
    };

    // Calculate Badge
    const unreadCount = hqNotices.filter(n => !readNoticeIds.includes(n.id)).length;
    const targetBranchId = 'main';

    const renderMainContent = () => {
        switch (activeTab) {
            case 'hqNotices':
                return <HQNoticeView notices={hqNotices} memberId={localAdmin?.id || ''} readNoticeIds={readNoticeIds} />;

            case 'members':
                return (
                    <MemberRoot
                        members={members}
                        branches={branches}
                        siteSettings={siteSettings}
                        onSelectMember={setSelectedMember}
                        selectedMemberId={selectedMember?.id}
                        onAddMember={addMember}
                        onUpdateMember={updateMember}
                    />
                );

            case 'checks':
                return null;

            case 'schedules':
                return (
                    <ScheduleManagement
                        books={books}
                        members={members}
                        branches={branches}
                        schedules={schedules}
                        onGenerate={(evts) => {
                            const batch = writeBatch(db);
                            evts.forEach(e => {
                                const ref = doc(collection(db, 'studentSchedules'));
                                batch.set(ref, { ...e, id: ref.id });
                            });
                            batch.commit().then(() => {
                                if (currentAdmin) {
                                    logActivity({
                                        type: 'schedule_change',
                                        action: 'create_schedules',
                                        actorId: currentAdmin.id,
                                        actorName: currentAdmin.name,
                                        actorRole: currentAdmin.role,
                                        details: JSON.stringify({ count: evts.length, sampleMemberId: evts[0]?.memberId }),
                                        branchId: targetBranchId || 'global'
                                    });
                                }
                            });
                        }}
                        onUpdate={(id, up) => {
                            updateDoc(doc(db, 'studentSchedules', id), up).then(() => {
                                if (currentAdmin) {
                                    logActivity({
                                        type: 'schedule_change',
                                        action: 'update_schedule',
                                        actorId: currentAdmin.id,
                                        actorName: currentAdmin.name,
                                        actorRole: currentAdmin.role,
                                        targetId: id,
                                        details: JSON.stringify(up),
                                        branchId: targetBranchId || 'global'
                                    });
                                }
                            });
                        }}
                        onDelete={(id) => {
                            deleteDoc(doc(db, 'studentSchedules', id)).then(() => {
                                if (currentAdmin) {
                                    logActivity({
                                        type: 'schedule_change',
                                        action: 'delete_schedule',
                                        actorId: currentAdmin.id,
                                        actorName: currentAdmin.name,
                                        actorRole: currentAdmin.role,
                                        targetId: id,
                                        details: 'Deleted schedule',
                                        branchId: targetBranchId || 'global'
                                    });
                                }
                            });
                        }}
                        onBulkDelete={async (ids) => {
                            const chunkSize = 500;
                            for (let i = 0; i < ids.length; i += chunkSize) {
                                const chunk = ids.slice(i, i + chunkSize);
                                const batch = writeBatch(db);
                                chunk.forEach(id => batch.delete(doc(db, 'studentSchedules', id)));
                                await batch.commit();
                            }
                            if (currentAdmin) {
                                logActivity({
                                    type: 'schedule_change',
                                    action: 'bulk_delete_schedules',
                                    actorId: currentAdmin.id,
                                    actorName: currentAdmin.name,
                                    actorRole: currentAdmin.role,
                                    details: JSON.stringify({ count: ids.length }),
                                    branchId: targetBranchId || 'global'
                                });
                            }
                        }}
                    />
                );

            case 'counsels': return <CounselRoot members={members} counsels={counsels} />;
            case 'notices': return <NoticeManagement notices={notices} currentAdmin={localAdmin!} />;
            case 'inquiry': return <InquiryRoot inquiries={inquiries} currentAdmin={localAdmin!} />;
            case 'success': return <SuccessRoot />;
            case 'academy-life': return <AcademyLifeRoot />;
            case 'reviews': return <ReviewsRoot />;
            case 'banners': return <BannersRoot />;
            // Settings tab removed as branch management is removed
            case 'logs': return <LogRoot currentAdmin={localAdmin!} branches={branches} />;
            case 'books':
                return (
                    <BookManagement
                        books={books}
                        siteSettings={siteSettings}
                        editingBook={editingBook}
                        onEditBook={setEditingBook}
                        onDeleteBook={async (b) => {
                            if (window.confirm('교재를 삭제하시겠습니까?')) {
                                await deleteDoc(doc(db, COLLECTIONS.BOOKS, b.id));
                            }
                        }}
                        onSubmit={(b) => setDoc(doc(db, COLLECTIONS.BOOKS, b.id), b)}
                        onCancelEdit={() => setEditingBook(null)}
                        currentUserId={localAdmin?.id}
                        currentUserRole={localAdmin?.role}
                        branchId={targetBranchId}
                    />
                );
            default: return null;
        }
    };




    // Check if branch is assigned
    if (!targetBranchId && currentAdmin?.role !== 'super_admin') {
        return (
            <div className="flex h-screen w-full flex-col items-center justify-center bg-slate-50 p-4">
                <div className="text-center space-y-4 max-w-md">
                    <div className="w-16 h-16 bg-slate-200 rounded-2xl flex items-center justify-center mx-auto text-slate-400">
                        <Users size={32} />
                    </div>
                    <h2 className="text-xl font-black text-slate-800">지점 정보가 없습니다</h2>
                    <p className="text-sm text-slate-500 font-medium leading-relaxed">
                        관리자 계정이지만 소속된 지점 정보가 확인되지 않습니다.<br />
                        슈퍼 관리자에게 지점 배정을 요청해주세요.
                    </p>
                    <div className="pt-4">
                        <button
                            onClick={async () => {
                                await auth.signOut();
                                window.location.href = '/';
                            }}
                            className="px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors shadow-sm"
                        >
                            로그아웃
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Teacher Menu Items (Settings removed)
    const managerMenuItems: MenuItem[] = [
        { id: 'hqNotices', label: '공지사항', icon: Bell, badge: unreadCount }, // New Tab with Badge
        { id: 'banners', label: '배너관리', icon: Sliders },
        { id: 'members', label: '학생관리', icon: Users },
        { id: 'books', label: '교재관리', icon: BookOpen },
        { id: 'schedules', label: '일정관리', icon: Calendar },
        { id: 'counsels', label: '상담관리', icon: Headphones },
        { id: 'notices', label: '공지관리', icon: MessageSquare },
        { id: 'inquiry', label: '교육상담', icon: MessageSquare },
        { id: 'success', label: '실적관리', icon: GraduationCap },
        { id: 'academy-life', label: '학원생활', icon: Camera },
        { id: 'reviews', label: '수강후기', icon: Star },
        { id: 'logs', label: '활동로그', icon: History },
    ];


    const branchName = CompanyName;

    return (
        <div className="flex h-screen w-full bg-slate-50 text-slate-800 overflow-hidden">
            <Sidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                menuItems={managerMenuItems}
                title="Teacher"
                branchName={branchName}
                adminName={localAdmin?.name}
                avatarUrl={localAdmin?.imageUrl}
                onLogout={async () => {
                    await auth.signOut();
                    window.location.href = '/';
                }}
                onGoHome={onGoHome}
                roleLabel="Teacher"
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={toggleSidebar}
            />
            <div className={`flex-1 h-full overflow-hidden transition-all duration-300 ${isSidebarCollapsed ? 'pl-20' : 'pl-0'}`}>
                {renderMainContent()}
            </div>
        </div>
    );
};

export default ManagerRoot;
